import { ROLE_NAME_PATTERN } from "@agentcall/core/type/admin_type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { roleApi } from "@/api/client/role"
import type { PermissionList200ResourcesItem, RoleList200RolesItem } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { toast } from "@/hooks/use-toast"
import { type Draft, PermissionGrid, toDraft } from "./PermissionGrid"

const roleFormSchema = z.object({
  organizationId: z.string().min(1, "組織を選択してください"),
  role: z
    .string()
    .min(1, "ロール名を入力してください")
    .regex(ROLE_NAME_PATTERN, "英小文字で始まり、英小文字・数字・アンダースコアのみ使用できます"),
  label: z.string().min(1, "表示名を入力してください").max(255, "255文字以内で入力してください"),
})

type RoleFormValues = z.infer<typeof roleFormSchema>

interface RoleFormModalProps {
  isOpen: boolean
  onClose: () => void
  role: RoleList200RolesItem | null
  resources: PermissionList200ResourcesItem[]
  organizationOptions: { value: string; label: string }[]
  onRefetch: () => void
}

export function RoleFormModal({
  isOpen,
  onClose,
  role,
  resources,
  organizationOptions,
  onRefetch,
}: RoleFormModalProps) {
  const isEdit = role !== null
  const [draft, setDraft] = useState<Draft>({})

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: {
      organizationId: "",
      role: "",
      label: "",
    },
  })

  useEffect(() => {
    if (!isOpen) return
    form.reset({
      organizationId: role?.organizationId ?? "",
      role: role?.role ?? "",
      label: role?.label ?? "",
    })
    setDraft(toDraft(role?.permissions))
  }, [isOpen, role, form])

  const createRoleMutation = roleApi.useRoleCreate({
    mutation: {
      onError: (error) =>
        toast({
          title: "ロールの保存に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })
  const updateRoleMutation = roleApi.useRoleUpdate({
    mutation: {
      onError: (error) =>
        toast({
          title: "ロールの保存に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })
  const isSubmitting = createRoleMutation.isPending || updateRoleMutation.isPending

  const onSubmit = (data: RoleFormValues) => {
    const mutationCallbacks = {
      onSuccess: () => {
        toast({
          title: isEdit ? "ロールを更新しました" : "ロールを作成しました",
        })
        onRefetch()
        onClose()
      },
    }

    if (isEdit) {
      // 予約ロールの誤更新を防ぐ
      if (!role.organizationId) {
        toast({
          title: "このロールは変更できません",
          variant: "destructive",
        })
        return
      }
      updateRoleMutation.mutate(
        {
          organizationId: role.organizationId,
          role: role.role,
          data: {
            label: data.label,
            permissions: draft,
          },
        },
        mutationCallbacks
      )
      return
    }

    createRoleMutation.mutate(
      {
        data: {
          organizationId: data.organizationId,
          role: data.role,
          label: data.label,
          permissions: draft,
        },
      },
      mutationCallbacks
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "ロールの編集" : "ロールの作成"}</DialogTitle>
          <DialogDescription>
            組織ごとに使えるロールと、そのロールが行える操作を設定します。
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="organizationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>組織</FormLabel>
                  <FormControl>
                    {isEdit ? (
                      <Input value={role.organizationName ?? ""} disabled />
                    ) : (
                      <Select
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="選択してください"
                        options={organizationOptions}
                      />
                    )}
                  </FormControl>
                  {isEdit && <FormDescription>ロールの所属組織は変更できません。</FormDescription>}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ロール名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="custom_auditor" disabled={isEdit} {...field} />
                  </FormControl>
                  <FormDescription>
                    英小文字で始まり、英小文字・数字・アンダースコアのみ使用できます。作成後は変更できません。
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>表示名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="監査担当者" {...field} />
                  </FormControl>
                  <FormDescription>
                    画面に表示される名前です。作成後も変更できます。
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <PermissionGrid resources={resources} draft={draft} onChange={setDraft} />

            <DialogFooter>
              <Button type="submit" variant="default" disabled={isSubmitting}>
                {isEdit ? "更新する" : "作成する"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                キャンセル
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
